/**
 * Post Author Display Utilities - PHASE 2
 * Badge Hierarchy & Author Visuals
 * DISPLAY-ONLY - does not affect permissions or roles
 * 
 * VISUAL PRIORITY ORDER:
 * 1. Founder (Highest)
 * 2. Admin
 * 3. Telegram Bot
 * 4. Premium User
 * 5. Free User (Default)
 */

/**
 * Get role priority (higher number = higher priority)
 * Used for determining which badge to show when multiple roles apply
 */
export const getRolePriority = (post) => {
  // Founder has highest priority
  if (post.author_email === 'gurjarvicky23@gmail.com' || 
      post.author_role === 'super_admin' || 
      post.author_name === 'FOUNDER') {
    return 4;
  }

  // Admin is second
  if (post.author_role === 'admin' || post.author_name?.toLowerCase().includes('admin')) {
    return 3;
  }

  // Telegram bot is third
  if (post.source === 'telegram_bot_v2' || post.source === 'telegram_bot_v2_strict') {
    return 2;
  }

  // Premium user is fourth
  if (post.author_role === 'premium_user') {
    return 1;
  }

  // Free user / default
  return 0;
};

/**
 * Get display author name with branding
 * Priority hierarchy enforced
 */
export const getDisplayAuthor = (post) => {
  // If custom display name is set (future admin control), use it
  if (post.display_author) {
    return post.display_author;
  }

  // PRIORITY 1: Founder (highest)
  if (post.author_email === 'gurjarvicky23@gmail.com' || 
      post.author_role === 'super_admin' || 
      post.author_name === 'FOUNDER') {
    return '@👑 OBSIDIANVOICE';
  }

  // PRIORITY 2: Admin
  if (post.author_role === 'admin' || post.author_name?.toLowerCase().includes('admin')) {
    return '@🛡️ Core Feed';
  }

  // PRIORITY 3: Telegram Bot
  if (post.source === 'telegram_bot_v2' || post.source === 'telegram_bot_v2_strict') {
    return '@🖤 EBONY SIGNAL';
  }

  // PRIORITY 4: Premium User (gets badge separately)
  // Regular users show their name

  // Fallback to original author_name
  return post.author_name || 'Unknown';
};

/**
 * Get styling for display author based on role priority
 * Returns { color, accentColor, fontWeight, textTransform, fontFamily }
 */
export const getAuthorStyle = (post) => {
  const priority = getRolePriority(post);

  // FOUNDER (Priority 4)
  if (priority === 4) {
    return {
      color: '#050505',
      accentColor: '#7A0C14',
      fontWeight: '700',
      textTransform: 'uppercase',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      letterSpacing: '0.03em',
      textShadow: '0 0 20px rgba(122, 12, 20, 0.3)'
    };
  }

  // ADMIN (Priority 3)
  if (priority === 3) {
    return {
      color: '#1C1C1C',
      accentColor: '#9A9A9A',
      fontWeight: '600',
      textTransform: 'none',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      letterSpacing: '0.02em',
      textShadow: '0 0 15px rgba(154, 154, 154, 0.2)'
    };
  }

  // TELEGRAM BOT (Priority 2)
  if (priority === 2) {
    return {
      color: '#0B0B0B',
      accentColor: '#C9A24D',
      fontWeight: '600',
      textTransform: 'uppercase',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      letterSpacing: '0.03em',
      textShadow: '0 0 15px rgba(201, 162, 77, 0.25)'
    };
  }

  // PREMIUM USER (Priority 1) - Gets diamond badge
  if (priority === 1) {
    return {
      color: '#E5E5E5',
      accentColor: '#7FD4FF',
      fontWeight: '600',
      textTransform: 'none',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      letterSpacing: '0.01em',
      textShadow: '0 0 10px rgba(127, 212, 255, 0.2)'
    };
  }

  // FREE USER (Default)
  return {
    color: '#A1A1A1',
    accentColor: '#6B6B6B',
    fontWeight: '500',
    textTransform: 'none',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    letterSpacing: '0.01em'
  };
};

/**
 * Get avatar background color based on role priority
 */
export const getAuthorAvatarColor = (post) => {
  const priority = getRolePriority(post);

  switch (priority) {
    case 4: // Founder
      return 'linear-gradient(135deg, #7A0C14 0%, #B91C1C 100%)';
    case 3: // Admin
      return 'linear-gradient(135deg, #4B5563 0%, #9CA3AF 100%)';
    case 2: // Telegram Bot
      return 'linear-gradient(135deg, #C9A24D 0%, #F59E0B 100%)';
    case 1: // Premium User
      return 'linear-gradient(135deg, #7FD4FF 0%, #3B82F6 100%)';
    default: // Free User
      return 'linear-gradient(135deg, #6B6B6B 0%, #9CA3AF 100%)';
  }
};

/**
 * Get role badge text (for verification display)
 */
export const getRoleBadge = (post) => {
  const priority = getRolePriority(post);

  switch (priority) {
    case 4:
      return 'FOUNDER';
    case 3:
      return 'ADMIN';
    case 2:
      return 'BOT';
    case 1:
      return 'PREMIUM';
    default:
      return null;
  }
};

/**
 * Check if post should show premium badge
 * Premium badge shows ONLY for premium_user role (not for higher roles)
 */
export const shouldShowPremiumBadge = (post) => {
  return post.author_role === 'premium_user' && getRolePriority(post) === 1;
};

/**
 * Get CSS classes for author container
 */
export const getAuthorContainerClass = (post) => {
  const priority = getRolePriority(post);
  
  return priority >= 2 
    ? 'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md' 
    : 'inline-flex items-center gap-1';
};
